/**
 * Created by fers on 05.02.16.
 */
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {injectIntl, defineMessages, IntlShape} from 'react-intl';

import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';

import Editor from '../fw/Editor.react';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'relationDefEdit.title', 'defaultMessage': 'Editing relation definition'}
});

interface RelationDefEditProps {
    intl: IntlShape;
    user: User;
}

class RelationDefEdit extends React.Component<RelationDefEditProps> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };
    typeName = 'relationDefs';

    constructor(props) {
        super(props);

        let data = props.user.organisation.relationDef;

        let state = {
            fromChannelId: null,
            toChannelId: null,
            fromEntityId: null,
            toEntityId: null,
        };

        if (data['fromChannel']) {
            state.fromChannelId = data['fromChannel'].id;
        }

        if (data['toChannel']) {
            state.toChannelId = data['toChannel'].id;
        }

        if (data['fromEntity']) {
            state.fromEntityId = data['fromEntity'].id;
        }

        if (data['toEntity']) {
            state.toEntityId = data['toEntity'].id;
        }

        this.state = state;
    }

    render() {
        let data = this.props.user.organisation.relationDef;
        let type = types[this.typeName];

        let fromEntities = [];
        let fromChannelEntities = [];
        let toEntities = [];
        let toChannelEntities = [];
        let fromFields = [];
        let toFields = [];

        if (this.state.fromChannelId !== null) {
            let channel = this.props.user.organisation.channels.edges.find(({node}) => node['id'] === this.state.fromChannelId).node;
            fromChannelEntities = channel.channelDef.channelEntityDefs.edges.map(({node}) => node);
            fromEntities = fromChannelEntities.map((node) => node.entityDef).filter((a) => a ? true : false);
        }

        if (this.state.toChannelId !== null) {
            let channel = this.props.user.organisation.channels.edges.find(({node}) => node['id'] === this.state.toChannelId).node;
            toChannelEntities = channel.channelDef.channelEntityDefs.edges.map(({node}) => node);
            toEntities = toChannelEntities.map((node) => node.entityDef).filter((a) => a ? true : false);
        }

        if (this.state.fromEntityId !== null && fromEntities !== null) {
            let channelEntityDef = fromChannelEntities.find((node) => node.entityDef && node.entityDef['id'] === this.state.fromEntityId);
            if (channelEntityDef) {
                fromFields = channelEntityDef.usedFieldDefs.edges.map(({node}) => node);
            }
        }

        if (this.state.toEntityId !== null && toEntities !== null) {
            let channelEntityDef = toChannelEntities.find((node) => node.entityDef && node.entityDef['id'] === this.state.toEntityId);
            if (channelEntityDef) {
                toFields = channelEntityDef.usedFieldDefs.edges.map(({node}) => node);
            }
        }

        return (
            <AppPanel breadcrumbParams={[
                {type, data},
                {type: types['organisations'], data: this.props.user.organisation}]
            } title={this.props.intl.formatMessage(messages.title)}>
                <Editor
                    columns={{
                        'name': {},
                        'type': {},
                        'description': {},
                        'fromChannel': {
                            values: this.props.user.organisation.channels.edges.map(({node}) => node),
                            display: (item) => {
                                if (item['name']) {
                                    return item['name'] + " / " + item['channelDef']['name'];
                                } else {
                                    return item['channelDef']['name'];
                                }
                            },
                            onChange: (e, key) => {
                                this.setState({fromChannelId: key});
                            },
                        },
                        'fromEntity': {
                            values: fromEntities,
                            display: (item) => {
                                return item['name'] + " (" + item['groupDef']['name'] + ")";
                            },
							// eslint-disable-next-line max-params
                            onChange: (e, key, payload, modifyValues) => {
                                this.setState({fromEntityId: key});

                                let channelEntityDef = fromChannelEntities.find((node) => node.entityDef && node.entityDef['id'] === key);
                                if (channelEntityDef && channelEntityDef.primaryFieldDef) {
                                    modifyValues('fromField', channelEntityDef.primaryFieldDef);
                                }
                            },
                        },
                        'fromField': {
                            values: fromFields,
                            display: (item) => {
                                return item['name'] + " (" + item['key'] + ": " + item['type'] + ")";
                            }
                        },
                        'toChannel': {
                            values: this.props.user.organisation.channels.edges.map(({node}) => node),
                            display: (item) => {
                                if (item['name']) {
                                    return item['name'] + " / " + item['channelDef']['name'];
                                } else {
                                    return item['channelDef']['name'];
                                }
                            },
                            onChange: (e, key) => {
                                this.setState({toChannelId: key});
                            },
                        },
                        'toEntity': {
                            values: toEntities,
                            display: (item) => {
                                return item['name'] + " (" + item['groupDef']['name'] + ")";
                            },
							// eslint-disable-next-line max-params
                            onChange: (e, key, payload, modifyValues) => {
                                this.setState({toEntityId: key});

                                let channelEntityDef = toChannelEntities.find((node) => node.entityDef && node.entityDef['id'] === key);
                                if (channelEntityDef && channelEntityDef.primaryFieldDef) {
                                    modifyValues('toField', channelEntityDef.primaryFieldDef);
                                }
                            },
                        },
                        'toField': {
                            values: toFields,
                            display: (item) => {
                                return item['name'] + " (" + item['key'] + ": " + item['type'] + ")";
                            }
                        },
                    }}
                    data={data}
                    fatQuery={Relay.QL `fragment on UpdateRelationDefPayload {
                        updatedNode {
                            id,
                            name,
                            type,
                            description,
                            organisation_id,
                            organisation {
                                id
                            },
                            fromChannel_id,
                            fromChannel {
                                id
                            },
                            toChannel_id,
                            toChannel {
                                id
                            },
                            fromEntity_id,
                            fromEntity {
                                id
                            },
                            toEntity_id,
                            toEntity {
                                id
                            },
                            fromField_id,
                            fromField {
                                id
                            },
                            toField_id,
                            toField {
                                id
                            }
                        },
                    }`}
                    metadata={type}
                    mutation={Relay.QL `mutation { updateRelationDef }`}
                    organisationId={this.props.user.organisation.realId}
                    parent={this.props.user.organisation}
                    redirectToList
                    urlMapping={urlMapping}
                />
            </AppPanel>
        );
    }
}


export default Relay.createContainer(wrapNotFound(injectIntl(RelationDefEdit), (props) => {
    return props.user.organisation && props.user.organisation.relationDef;
}), {
    initialVariables: {
        organisationId: '',
        relationDefId: '',
    },
    prepareVariables (variables) {
		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    relationDef(id: $relationDefId) {
                        id,
                        name,
                        type,
                        description,
                        fromChannel_id,
                        toChannel_id,
                        fromChannel {
                            id,
                            name,
                            channelDef {
                                id,
                                name,
                            }
                        },
                        toChannel {
                            id,
                            name,
                            channelDef {
                                id,
                                name,
                            }
                        },
                        fromEntity {
                            id,
                            name
                        },
                        toEntity {
                            id,
                            name
                        },
                        fromField {
                            id,
                            name
                        },
                        toField {
                            id,
                            name
                        },
                    },
                    channels(first: 2000) {
                        edges {
                            node {
                                id,
                                name,
                                channelDef {
                                    id,
                                    name,
                                    channelEntityDefs(first: 20000) {
                                        edges {
                                            node {
                                                id,
                                                entityDef {
                                                    id,
                                                    name,
                                                    key,
                                                    groupDef {
                                                        id,
                                                        name,
                                                        key
                                                    }
                                                },
                                                primaryFieldDef {
                                                    id,
                                                    realId,
                                                    name,
                                                    entityDef_id,
                                                },
                                                usedFieldDefs(first: 20000) {
                                                    edges {
                                                        node {
                                                            id,
                                                            name,
                                                            type,
                                                            key
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
            }
        `,
    },
});
